import {
  AddExtraTableToPartyCoreAction,
  ServiceCoreLocalAction,
  TMCoreAction,
} from 'state/service/appTs/types';
import { SERVER_TABLE_TAG } from 'constants/service/serversConstants';
import { ServiceStateStrict } from 'state/service/app';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { update as tmClientUpdate } from 'state/service/appTs/tmClient';

/**
 * Used in devlopment as we migrate Elm over to TypeScript.
 *
 * Run the following command to run tests and generate coverage on the fly:
 * pnpm test_vitest_coverage test/state/service/localCoreActions --watch
 */
export default function serviceCoreLocal(
  model: ServiceStateStrict,
  action: ServiceCoreLocalAction
): ServiceStateStrict {
  const timestamp = action.payload.timestamp;
  function localUpdate(actions: TMCoreAction[]): ServiceStateStrict {
    return tmClientUpdate(model, {
      type: 'LocalAction',
      actions,
      timestampSecs: timestamp,
    });
  }

  switch (action.type) {
    case 'SERVICE_CORE_REPLACE_TABLES_FOR_PARTY':
      return localUpdate([{ type: 'AssignPartyToTable', partyAndTable: action.payload }]);
    case 'SERVICE_CORE_UPDATE_PARTY_STATE':
      return localUpdate([{ type: 'UpdatePartyState', partyAndState: action.payload }]);
    case 'SERVICE_CORE_REMOVE_SERVER':
      return localUpdate([{ type: 'RemoveServer', tableIds: action.payload.tableIds }]);
    case 'SERVICE_CORE_CUT_SERVER':
      return localUpdate([{ type: 'CutServer', businessServerId: action.payload.id }]);
    case 'SERVICE_CORE_ASSIGN_BUSINESS_SERVER_TO_TABLE':
      return localUpdate([
        { type: 'AssignBusinessServerToTable', businessServerAndTables: action.payload },
      ]);
    case 'SERVICE_CORE_REMOVE_PARTY_FROM_TABLE':
      return localUpdate([{ type: 'RemovePartyFromTable', partyAndTable: action.payload }]);
    case 'SERVICE_CORE_ADD_SERVER_WITH_NAME':
      return localUpdate([
        { type: 'AddAvailableTableTag', key: SERVER_TABLE_TAG, val: action.payload.name },
      ]);
    case 'SERVICE_CORE_REMOVE_SERVER_WITH_NAME':
      return localUpdate([
        { type: 'RemoveAvailableTableTag', key: SERVER_TABLE_TAG, val: action.payload.name },
      ]);
    case 'SERVICE_CORE_MOVE_TABLE':
      return localUpdate([{ type: 'MoveTable', tableAndPosition: action.payload }]);
    case 'SERVICE_CORE_UPDATE_TABLE_STATE':
      return localUpdate([{ type: 'UpdateTableState', tableAndState: action.payload }]);
    case 'SERVICE_CORE_UPDATE_PARTY':
      return localUpdate([{ type: 'UpdateParty', partyUpdate: action.payload }]);
    case 'SERVICE_CORE_REMOVE_WALK_IN_PARTY':
      return localUpdate([{ type: 'RemoveWalkInParty', partyId: action.payload.partyId }]);
    case 'SERVICE_CORE_SEAT_WALK_IN_PARTY': {
      const { walkInPartyDescription } = action.payload;
      const partyId = walkInPartyDescription.id;
      const addTableToPartyActions: AddExtraTableToPartyCoreAction[] = action.payload.tableIds.map(
        (tableId) => ({
          type: 'AddExtraTableToParty',
          partyAndTable: { partyId, tableId },
        })
      );
      return localUpdate([
        { type: 'AddWalkInParty', partyDescription: walkInPartyDescription },
        ...addTableToPartyActions,
        {
          type: 'UpdatePartyState',
          partyAndState: {
            partyId,
            state: walkInPartyDescription.expectedReservationTime === '' ? 'SEATED' : 'EXPECTED',
            secondaryState: walkInPartyDescription.secondaryState,
            timestamp,
          },
        },
      ]);
    }
    case 'SERVICE_CORE_ADD_EXTRA_TABLE_TO_PARTY':
      return localUpdate([{ type: 'AddExtraTableToParty', partyAndTable: action.payload }]);
    case 'SERVICE_CORE_ADD_MULTIPLE_EXTRA_TABLES_TO_PARTY': {
      const { partyId, tableIds } = action.payload;
      return localUpdate(
        tableIds.map((tableId) => ({
          type: 'AddExtraTableToParty',
          partyAndTable: { partyId, tableId },
          timestamp,
        }))
      );
    }
    case 'SERVICE_CORE_ADD_WALK_IN_PARTY': {
      const { timestamp, ...payload } = action.payload;
      return localUpdate([{ type: 'AddWalkInParty', partyDescription: payload }]);
    }
    case 'SERVICE_CORE_MARK_TEXT_MESSAGES_AS_READ':
      return localUpdate([{ type: 'MarkTextMessagesAsRead', partyId: action.payload.partyId }]);
    case 'SERVICE_CORE_RESET_TABLE_POSITIONS':
      return localUpdate([{ type: 'ResetTablePositions' }]);
    // istanbul ignore next
    default:
      return assertNever(action);
  }
}
